import {useContext, useEffect, useMemo, useState} from 'react'

import PropTypes from 'prop-types'

import jsCookie from '@s-ui/js/lib/cookie'
import {toQueryString} from '@s-ui/js/lib/string'
import AtomBadge, {atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {MoleculeTab, MoleculeTabs, moleculeTabsTypes, moleculeTabsVariants} from '@s-ui/react-molecule-tabs'
import {useLocation} from '@s-ui/react-router'
import {SaitamaAd} from '@adv-mt/advertising-publift'
import ActionSaveNews from '@adv-mt/mt-actions-save-news'
import AdDisplay from '@adv-mt/mt-ad-display'
import EmptyCase, {emptyCaseDefaultSizes} from '@adv-mt/mt-empty-case-default'
import ErrorMessage from '@adv-mt/mt-error-message'
import FeedArticles from '@adv-mt/mt-feed-articles'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import ListPagination from '@adv-mt/mt-list-pagination'
import MotorTagCloud from '@adv-mt/mt-list-tagcloud'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import NewsletterSubscription from '@adv-mt/mt-newsletter-subscription'
import SearchNews from '@adv-mt/mt-search-news'
import SectionBasic, {sectionBasicMargins} from '@adv-mt/mt-section-basic'
import SharedSmartbanner, {sharedSmartbannerPositions} from '@adv-mt/mt-shared-smartbanner'
import IconBookmarkOutline from '@adv-mt/mt-svg-icons/lib/BookmarkOutline'
import IconBookmarkOutlinePlus from '@adv-mt/mt-svg-icons/lib/BookmarkOutlinePlus'
import IconFileDocumentOutline from '@adv-mt/mt-svg-icons/lib/FileDocumentOutline'
import TitleFeedArticles from '@adv-mt/mt-title-news'
import MotorTrackingCriteo from '@adv-mt/mt-tracking-criteo'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {adaptDomainNewsFeed} from './adapters/adaptDomainNewsFeed.js'
import {getInitialProps} from './getInitialProps.js'

const getNewsLinkFactoryWithOrigin = origin => props => <LinkFactory {...props} state={{origin}} />

const TAB_ALL = 'all'
const TAB_SAVED = 'saved'

// MoleculeTabs uses 1-based tab numbers
const TAB_NUMBER = {
  [TAB_ALL]: 1,
  [TAB_SAVED]: 2
}

const NewsList = ({
  hasVisitedSavedNewsTab: initialHasVisitedSavedNewsTab = false,
  hasServerError,
  initialPageData: {breadcrumb, category, helmet, h1, page, pageName, searchTerm, tag, tagclouds, section} = {},
  isNewsBookmarksEnabled = false,
  newsFeed,
  router,
  savedArticleIds = [],
  loadThirdParties
}) => {
  const {
    pathname,
    query: {pg: pageQuery, ...queryParams}
  } = useLocation()

  const {domain, i18n} = useContext(Context)
  const {page: trackPageView} = useAnalyticsTrack()
  const config = domain.get('config')
  const {NEWSLETTER_BANNER_ENABLED, SAVED_NEWS_EMPTY_CASE_IMAGE: emptyCaseImage} = config.get('editorial')

  const [activeTab, setActiveTab] = useState(TAB_ALL)
  const [savedNewsContent, setSavedNewsContent] = useState(null)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  const [localSavedIds, setLocalSavedIds] = useState(savedArticleIds)

  const {
    COOKIES: {HAS_VISITED_SAVED_NEWS_TAB},
    COOKIE_EXPIRATIONS
  } = config.get('COMMON')
  const [hasVisitedSavedNewsTab, setHasVisitedSavedNewsTab] = useState(initialHasVisitedSavedNewsTab)

  const savedIdsSet = new Set(localSavedIds)

  const renderCardActionsForList = isNewsBookmarksEnabled
    ? item => (
        <ActionSaveNews
          newsId={item.articleId}
          isSaved={savedIdsSet.has(item.articleId)}
          pageName={pageName}
          pageType={newsPageType}
        />
      )
    : undefined

  const renderCardActionsForSaved = isNewsBookmarksEnabled
    ? item => (
        <ActionSaveNews
          key={`saved-${item.articleId}`}
          newsId={item.articleId}
          isSaved
          pageName={pageName}
          pageType={newsPageType}
        />
      )
    : undefined

  const handleTabChange = (_, {numTab}) => {
    const isSavedTab = numTab === TAB_NUMBER[TAB_SAVED]
    setActiveTab(isSavedTab ? TAB_SAVED : TAB_ALL)

    if (isSavedTab) {
      trackPageView({event: newsSavedViewedEvent, data: newsSavedViewedData})

      if (!hasVisitedSavedNewsTab) {
        jsCookie.set(HAS_VISITED_SAVED_NEWS_TAB, true, {expires: COOKIE_EXPIRATIONS.ONE_YEAR})
        setHasVisitedSavedNewsTab(true)
      }
    }
  }

  useEffect(() => {
    if (activeTab === TAB_SAVED) setIsLoadingSaved(true)

    domain
      .get('get_saved_news_ids_use_case')
      .execute()
      .then(([idsError, ids]) => {
        setLocalSavedIds(ids ?? [])
        if (activeTab !== TAB_SAVED) return

        if (idsError || !ids?.length) {
          setSavedNewsContent(null)
          setIsLoadingSaved(false)
          return
        }
        return domain
          .get('get_news_by_ids_news_use_case')
          .execute({ids})
          .then(([error, response]) => {
            if (!error && response) {
              const idsOrder = new Map(ids.map((id, i) => [id, i]))
              const sortedItems = [...response.items].sort(
                (a, b) => (idsOrder.get(a.articleId) ?? Infinity) - (idsOrder.get(b.articleId) ?? Infinity)
              )
              setSavedNewsContent({...response, items: sortedItems})
            } else setSavedNewsContent(null)
          })
      })
      .finally(() => setIsLoadingSaved(false))
  }, [activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

  const newsFeedUpdated = adaptDomainNewsFeed({
    domainConfig: config,
    newsFeed
  })

  const {SMARTBANNER_SOURCE_IDS} = config.get('smartbanner')

  const {
    HIT_INFORMATIONS: {SMARTBANNER_POSITION_TOP: hitInformationSmartbannerPositionTop},
    HIT_ORIGINS: {
      NEWS_LIST: hitOriginNewsList,
      NEWS_SAVED_LIST: hitOriginNewsSavedList,
      SMARTBANNER_NEWS_LIST: hitOriginSmartbannerNewsList
    },
    TRACKING_DATA_BY_PAGE: {
      NEWS: {page_type: newsPageType},
      NEWS_SAVED: {event: newsSavedViewedEvent, ...newsSavedViewedData}
    }
  } = config.get('tracking')

  const newsListLinkFactory = useMemo(() => getNewsLinkFactoryWithOrigin(hitOriginNewsList), [hitOriginNewsList])
  const savedNewsListLinkFactory = useMemo(
    () => getNewsLinkFactoryWithOrigin(hitOriginNewsSavedList),
    [hitOriginNewsSavedList]
  )

  const appName = config.get('APP_NAME')
  const noResultsText = i18n.t(`ERROR_MESSAGE_NO_RESULTS_${appName}_TEXT`)
  const serverErrorText = i18n.t(`ERROR_MESSAGE_SERVER_ERROR_${appName}_TEXT`)

  const {PubLeft1, PubLeft2, ADS_LIST_POSITIONS} = config.get('advertising')
  const isHigherThanTabletResolution = useMediaQuery(useMediaQueryBreakpoints.lg)
  const addAdvertisingRectangleBlock = () => {
    return (
      (Boolean(ADS_LIST_POSITIONS[PubLeft1]) || Boolean(ADS_LIST_POSITIONS[PubLeft2])) &&
      loadThirdParties &&
      isHigherThanTabletResolution && (
        <div className="mt-NewsList-AdvertisingRectangle">
          <>
            <SaitamaAd id={ADS_LIST_POSITIONS[PubLeft1].saitama.id} />
            <SaitamaAd id={ADS_LIST_POSITIONS[PubLeft2].saitama.id} />
          </>
        </div>
      )
    )
  }

  const renderSavedContent = () => {
    if (savedNewsContent?.items?.length > 0) {
      return (
        <>
          <TitleFeedArticles h1={`${savedNewsContent.items.length} ${i18n.t('NEWS_LIST_SAVED_COUNT')}`} />
          <FeedArticles
            initialNewsSearchFeed={adaptDomainNewsFeed({domainConfig: config, newsFeed: savedNewsContent})}
            linkFactory={savedNewsListLinkFactory}
            renderCardActions={renderCardActionsForSaved}
          />
        </>
      )
    }

    return (
      <EmptyCase
        imageUrl={emptyCaseImage}
        imageSize={emptyCaseDefaultSizes.LARGE}
        isPortrait
        title="NEWS_LIST_SAVED_EMPTY_TITLE"
        textNode={
          <>
            {i18n.t('NEWS_LIST_SAVED_EMPTY_TEXT_BEFORE')} <IconBookmarkOutlinePlus size={ATOM_ICON_SIZES.medium} />{' '}
            {i18n.t('NEWS_LIST_SAVED_EMPTY_TEXT_AFTER')}
          </>
        }
      />
    )
  }

  if (hasServerError) {
    return (
      <>
        <div className="mt-NewsList">
          <ErrorMessage text={serverErrorText} />
        </div>
      </>
    )
  }

  const {NEWS_LIST_PAGINATION_FIRST_NUMBER: firstPageNumber} = config.get('COMMON')

  const getUrlByPageNumber = ({pageNumber}) => {
    const isFirstPage = pageNumber === firstPageNumber
    const urlQueryParams = {
      ...queryParams,
      ...(pageNumber && !isFirstPage && {pg: pageNumber})
    }
    const urlQueryString = toQueryString(urlQueryParams)

    return urlQueryString ? `${pathname}?${urlQueryString}` : pathname
  }

  const {next, previous} = newsFeedUpdated.pagination

  const links = [
    {rel: 'next', href: next},
    {rel: 'prev', href: previous}
  ].filter(item => !!item.href)

  const paginationCurrentPage = page && page > 0 ? page : firstPageNumber
  const savedNewsTabLabel = hasVisitedSavedNewsTab ? (
    i18n.t('NEWS_LIST_TAB_SAVED')
  ) : (
    <span className="mt-NewsList-savedTabLabel">
      {i18n.t('NEWS_LIST_TAB_SAVED')}
      <AtomBadge label={i18n.t('NEWS_BOOKMARKS.TAB_BADGE_NEW')} size={atomBadgeSizes.SMALL} type={atomBadgeTypes.NEW} />
    </span>
  )

  return (
    <>
      <SharedSmartbanner
        pageName={pageName}
        position={sharedSmartbannerPositions.STICKY}
        hasTopbarOffset={true}
        source={SMARTBANNER_SOURCE_IDS.EDITORIAL}
        trackingOrigin={hitOriginSmartbannerNewsList}
        trackingInformation={hitInformationSmartbannerPositionTop}
      />
      <Layout.Basic linkFactory={LinkFactory}>
        <main className="mt-NewsList">
          <SectionBasic margin={sectionBasicMargins.xxl}>
            <SearchNews />
          </SectionBasic>

          {isNewsBookmarksEnabled && (
            <SectionBasic margin={sectionBasicMargins.xxl}>
              <MoleculeTabs
                onChange={handleTabChange}
                type={moleculeTabsTypes.HORIZONTAL}
                variant={moleculeTabsVariants.HIGHLIGHTED}
              >
                <MoleculeTab
                  label={i18n.t('NEWS_LIST_TAB_ALL')}
                  active={activeTab === TAB_ALL}
                  icon={<IconFileDocumentOutline size={ATOM_ICON_SIZES.medium} />}
                />
                <MoleculeTab
                  label={savedNewsTabLabel}
                  active={activeTab === TAB_SAVED}
                  icon={<IconBookmarkOutline size={ATOM_ICON_SIZES.medium} />}
                />
              </MoleculeTabs>
            </SectionBasic>
          )}

          {activeTab === TAB_ALL &&
            (newsFeedUpdated.items?.length ? (
              <>
                <SectionBasic margin={sectionBasicMargins.xxl}>
                  <TitleFeedArticles h1={h1} />
                  <FeedArticles
                    tag={tag}
                    category={category}
                    page={page}
                    initialNewsSearchFeed={newsFeedUpdated}
                    linkFactory={newsListLinkFactory}
                    renderCardActions={renderCardActionsForList}
                  />
                </SectionBasic>
                <SectionBasic margin={sectionBasicMargins.xxl}>
                  <ListPagination
                    currentPage={paginationCurrentPage}
                    totalPages={newsFeedUpdated.totalPages}
                    linkFactory={LinkFactory}
                    createUrl={getUrlByPageNumber}
                  />
                </SectionBasic>
                <Helmet link={links} />
              </>
            ) : (
              <ErrorMessage text={noResultsText} />
            ))}

          {activeTab === TAB_SAVED && isNewsBookmarksEnabled && (
            <SectionBasic margin={sectionBasicMargins.xxl}>{isLoadingSaved ? null : renderSavedContent()}</SectionBasic>
          )}
          {NEWSLETTER_BANNER_ENABLED && (
            <SectionBasic margin={sectionBasicMargins.xxl}>
              <NewsletterSubscription pageName={pageName} />
            </SectionBasic>
          )}

          <SectionBasic margin={sectionBasicMargins.xxl}>{addAdvertisingRectangleBlock()}</SectionBasic>
          <SectionBasic margin={sectionBasicMargins.xxl}>
            <MotorTagCloud initialTagclouds={tagclouds} pageName={pageName} linkFactory={LinkFactory} />
          </SectionBasic>
          <SectionBasic margin={sectionBasicMargins.xxl}>
            <Breadcrumb items={breadcrumb} linkFactory={LinkFactory} />
          </SectionBasic>
        </main>

        <Helmet {...helmet} />
        {loadThirdParties && (
          <>
            <TrackingPageView
              pageName={pageName}
              pageNumber={page}
              tag={tag}
              category={category}
              searchTerm={searchTerm}
              searchResultsCount={newsFeed?.count}
            />
            <AdDisplay pageName={pageName} category={category} tag={tag} section={section} searchTerm={searchTerm} />
            <MotorTrackingCriteo />
          </>
        )}
      </Layout.Basic>
    </>
  )
}

NewsList.displayName = 'NewsList'

NewsList.propTypes = {
  hasVisitedSavedNewsTab: PropTypes.bool,
  hasServerError: PropTypes.bool,
  initialPageData: PropTypes.object,
  isLogged: PropTypes.bool,
  isNewsBookmarksEnabled: PropTypes.bool,
  loadThirdParties: PropTypes.bool,
  newsFeed: PropTypes.object,
  params: PropTypes.object,
  router: PropTypes.object.isRequired,
  savedArticleIds: PropTypes.arrayOf(PropTypes.string)
}

NewsList.getInitialProps = getInitialProps

export default NewsList
